import { Component, EventEmitter, Input, OnChanges, OnInit, Output, inject } from '@angular/core';
import {FormBuilder, FormGroup, Validators} from '@angular/forms';

import {Observable} from 'rxjs';

import {DropdownItem, ModalService, TimeValidators, TransportType} from '@shared';

import {FeatureFlags, FeatureFlagService} from '@core';

import {EmployeeService, EmployeeView, ProblemInstanceService, SkillLevelService} from 'planner-shared';
import {ShiftSplitModalComponent} from "../shift-split-modal/shift-split-modal.component";
import {RosterService} from "../../shared/services/roster/roster.service";

@Component({
    selector: 'app-employee-edit',
    templateUrl: './employee-edit.component.html',
    styleUrls: ['./employee-edit.component.scss'],
    standalone: false
})
export class EmployeeEditComponent implements OnInit, OnChanges {
    private readonly formBuilder = inject(FormBuilder);
    private readonly featureFlagService = inject(FeatureFlagService);
    private readonly employeeService = inject(EmployeeService);
    private readonly modalService = inject(ModalService);
    private readonly skillLevelService = inject(SkillLevelService);
    private readonly rosterService = inject(RosterService);
    private readonly problemInstanceService = inject(ProblemInstanceService);

    @Input() public employee: EmployeeView;
    @Input() public updating: boolean;
    @Output() public changed = new EventEmitter<void>();
    @Output() public shiftSplitEmitter = new EventEmitter<void>();
    public shiftTimeForm: FormGroup;
    public transportDropdownItems: DropdownItem[];
    public rosterDropdownItems: Observable<DropdownItem[]>;
    public skillLevelDropdownItems$: Observable<DropdownItem[]>;
    public showRoster: boolean;
    public isRideSharingEnabledOnClient: boolean;
    public transportType = TransportType;
    public shiftSplitFeatureEnabled: Observable<boolean>;
    public isCriticalRosterFeatureEnabled: Observable<boolean>;
    public rosterTooltipMessage: string;
    private initialized = false;

    constructor() {
        this.transportDropdownItems = [
            <DropdownItem>{value: TransportType.Drive, label: 'DRIVE'},
            <DropdownItem>{value: TransportType.Bicycle, label: 'BICYCLE'},
            <DropdownItem>{value: TransportType.Walk, label: 'WALK'}
        ];
    }

    ngOnInit(): void {
        this.skillLevelDropdownItems$ = this.skillLevelService.getSkillLevelDropdownItems();
        this.shiftTimeForm = this.initShiftTimeForm();
        this.problemInstanceService.rosterDepartmentIsPartOfInstance()
            .subscribe(result => {
                this.showRoster = result.valueOf();
                if (this.showRoster) {
                    this.rosterDropdownItems = this.rosterService.getRosterDropdownItems();
                }
            });
        this.shiftSplitFeatureEnabled = this.featureFlagService.isEnabled(FeatureFlags.ShiftSplit);
        this.isCriticalRosterFeatureEnabled = this.featureFlagService.isEnabled(FeatureFlags.CriticalRosterPriority);

        this.problemInstanceService.getProblemInstance()
            .subscribe(problemInstanceResult => {
                this.isRideSharingEnabledOnClient
                    = problemInstanceResult.problemInstance.officeInfo.rideSharingEnabledOnClientSide;
            });
        this.initialized = true;
        this.updateRosterTooltip();
    }

    ngOnChanges(): void {
        this.revert();
        this.shiftTimeForm = this.initShiftTimeForm();
        this.updateRosterTooltip();
    }

    public skillLevelChanged(): void {
        this.validateEmployee();
        this.changed.emit();
        this.skillLevelService
            .getSkillLevelDescription(this.employee.skillLevelId)
            .subscribe(skillLevelDescription => (this.employee.skillLevelDescription = skillLevelDescription));
    }

    public transportChanged(): void {
        this.validateEmployee();
        if (this.employee.transport !== TransportType.Drive && this.employee.transport !== TransportType.Walk) {
            this.employee.rideSharing = false;
        }
        this.changed.emit();
    }

    public rosterChanged(): void {
        this.employee.rosterDepartmentName = this.rosterService.getRosterNameById(this.employee.rosterDepartmentId);
        this.validateEmployee();
        this.changed.emit();
        this.updateRosterTooltip();
    }

    public toggleActiveChanged(): void {
        this.validateEmployee();
        if (!this.employee.isActive) {
            this.employee.rideSharing = false;
        }
        this.updateRideSharingRole();
        this.changed.emit();
    }

    public toggleRideSharingChanged(): void {
        this.validateEmployee();
        if (!this.employee.rideSharing) {
            this.employee.rideSharingRole = null;
        }
        this.updateRideSharingRole();
        this.changed.emit();
    }

    public fromTimeChanged(): void {
        this.validateEmployee();
        this.employee.fromTime = this.shiftTimeForm.value.fromTime;
        this.changed.emit();
        this.employee.hasError = !this.shiftTimeForm.valid;
    }

    public toTimeChanged(): void {
        this.validateEmployee();
        this.employee.toTime = this.shiftTimeForm.controls['toTime'].value;
        this.changed.emit();
        this.employee.hasError = !this.shiftTimeForm.valid;
    }

    public revert(): void {
        if (this.shiftTimeForm !== undefined && this.shiftTimeForm.dirty) {
            this.shiftTimeForm.patchValue(this.employee);
            this.shiftTimeForm.markAsPristine();
        }
    }

    public updateRideSharingRole(): void {
        switch (this.employee.transport) {
        case TransportType.Drive:
            this.employee.rideSharingRole = `DRIVER`;
            break;
        case TransportType.Walk:
            this.employee.rideSharingRole = `PASSENGER`;
            break;
        default:
            this.employee.rideSharingRole = null;
        }
    }

    public validateEmployee() {
        this.employee.editing = !this.shiftTimeForm.invalid;
        this.employee.hasError = this.shiftTimeForm.invalid;
    }

    public openSplitShiftModal() {
        this.modalService.openCustomModal(
            ShiftSplitModalComponent,
            () => this.shiftSplitEmitter.emit(),
            null,
            this.employee,
            false
        );
    }

    private initShiftTimeForm(): FormGroup {
        this.updateRideSharingRole();
        const employeesWithSameId = this.constructEmployeesWithSameIds();
        return this.formBuilder.group(
            {
                fromTime: [
                    this.employee.fromTime,
                    Validators.compose([Validators.required, Validators.minLength(5), Validators.maxLength(5)])
                ],
                toTime: [
                    this.employee.toTime,
                    Validators.compose([Validators.required, Validators.minLength(5), Validators.maxLength(5)])
                ],
            },
            {
                validators: [TimeValidators.dateAndTimeGreaterThanOrEqual(
                    this.employee.fromDate,
                    'fromTime',
                    this.employee.toDate,
                    'toTime'
                ),
                TimeValidators.timeOverlaps(employeesWithSameId, this.employee, 'fromTime', 'toTime')
                ]
            }
        );
    }

    private constructEmployeesWithSameIds(): EmployeeView[] {
        let employeesAsList: EmployeeView[] = [];
        this.employeeService.getEmployees().subscribe(
            employees => employeesAsList = employees as EmployeeView[]
        );
        return employeesAsList.filter(
            employee => (employee.employeeId === this.employee.employeeId && employee.isActive)
        );
    }

    private updateRosterTooltip(): void {
        if (this.initialized) {
            this.isCriticalRosterFeatureEnabled.subscribe(isCriticalFeatureEnabled => {
                if (!this.employee.rosterDepartmentName && this.showRoster && isCriticalFeatureEnabled) {
                    this.rosterService.areDifferentRosterTasksOnEmployee(this.employee)
                        .subscribe(value => this.rosterTooltipMessage = value ? "EMPLOYEE_ROSTER_MATCHING_UPDATE_WARNING" : "EMPLOYEE_ROSTER_UPDATE_WARNING");
                }
            });
        }
    }
}
